import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Modal } from "./Modal";
import { HeaderModal } from "./Header";
import { Body } from "./Body";
import { FooterModal } from "./Footer";

import closeIcon from "assets/images/close-icon.svg";
import ChangeVijilizeImageModal from "./ChangeVijilizeImageModal";

interface ReAnalyzeModalProps {
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>>;
  vijilizeId?: string;
  onReanalyze?: (instruction: string, newImageUrl?: string) => void;
  targetPatentId?: string;
  projectId?: string;
  imageS3Path?: string;
}

const ReAnalyzeModal = ({ isShow, setIsShow, vijilizeId, onReanalyze, targetPatentId, projectId, imageS3Path }: ReAnalyzeModalProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [instruction, setInstruction] = useState("");
  const [imageChanged, setImageChanged] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState<string>(""); // S3 path for reanalysis
  const [showChangeImageModal, setShowChangeImageModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>(""); // Preview URL for display

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isShow) {
      setInstruction("");
      setImageChanged(false);
      setNewImageUrl("");
      setThumbnailUrl("");
    }
  }, [isShow]);

  const onCloseModal = () => {
    setIsShow(false);
    setInstruction("");
    setImageChanged(false);
    setNewImageUrl("");
    setShowChangeImageModal(false);
    setThumbnailUrl("");
  };

  const handleChangeImage = () => {
    setShowChangeImageModal(true);
  };

  const handleImageChanged = (newUrl: string, previewUrl?: string) => {
    setImageChanged(true);
    setNewImageUrl(newUrl); // Store S3 path for reanalysis
    setThumbnailUrl(previewUrl || newUrl); // Use preview URL if provided, otherwise use the URL itself
    setShowChangeImageModal(false);
  };

  const handleReanalyze = async () => {
    if (!instruction && !imageChanged) {
      return;
    }

    // Close modal immediately
    onCloseModal();
    
    // Call the onReanalyze callback if provided
    // The callback will handle status update, navigation, and background processing
    if (onReanalyze) {
      try {
        await onReanalyze(instruction, imageChanged ? newImageUrl : undefined);
        
        // Clear the sessionStorage cache to force fresh data load
        sessionStorage.removeItem('vijilizeClaimData');
      } catch (error) {
        console.error("Error reanalyzing:", error);
      }
    }
  };

  // Check if reanalyze button should be enabled
  const isReanalyzeEnabled = instruction.trim().length > 0 || imageChanged;

  return (
    <>
      <Modal className={"modal-wrapper max-w-[600px]"} isShow={isShow}>
        <form>
          <HeaderModal
            title={"ReAnalyze"}
            isShowHeader={true}
            className="modal-header pt-6 px-6 border-b border-[#EAECF0] pb-5"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg text-[#101828] font-interSemiBold leading-none pb-1.5">
                ReAnalyze
              </h2>
              {/* Close Icon */}
              <img
                className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1 -mt-2"
                id="modal-toggle"
                src={closeIcon}
                onClick={onCloseModal}
                alt="close icon"
              />
            </div>

            <p className="text-sm text-[#475467]">
              Add instructions or change the image to reanalyze
            </p>
          </HeaderModal>

          <Body
            title=""
            isShowBody={true}
            className="modal-body px-6 pt-5 pb-5"
          >
            <div className="space-y-5">
              {/* Instruction Field */}
              <div>
                <label className="block text-sm font-interSemiBold text-[#344054] mb-2">
                  Instruction (Optional)
                </label>
                <textarea
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder="Enter any specific instructions for reanalysis..."
                  className="w-full p-3 text-sm border border-[#D0D5DD] rounded-lg focus:border-[#1751D0] focus:outline-none focus:ring-2 focus:ring-[#E8E6FE] resize-none"
                  rows={4}
                />
                <p className="text-xs text-[#667085] mt-1">
                  Provide specific instructions to guide the reanalysis process
                </p>
              </div>

              {/* Change Image Section */}
              <div>
                <label className="block text-sm font-interSemiBold text-[#344054] mb-2">
                  New Image
                </label>
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={handleChangeImage}
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#1751D0] hover:bg-[#113B98] text-white rounded-lg text-sm font-interSemiBold transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                    Change Image
                  </button>
                  
                  {/* Thumbnail Preview */}
                  {imageChanged && thumbnailUrl && (
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <img
                          src={thumbnailUrl}
                          alt="Selected image"
                          className="w-20 h-20 object-cover rounded-lg border-2 border-[#1751D0]"
                        />
                        <div className="absolute -top-2 -right-2 bg-[#1751D0] rounded-full p-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      </div>
                      <span className="text-sm text-[#1751D0] font-interMedium">
                        New image selected
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-[#667085] mt-1">
                  Select a different image for reanalysis
                </p>
              </div>
            </div>
          </Body>

          <FooterModal
            title=""
            isShowFooter={true}
            className="modal-footer pt-5 px-6 pb-4 border-t border-[#EAECF0]"
          >
            <div className="flex items-center gap-x-3 justify-end">
              <button
                onClick={onCloseModal}
                type="button"
                className="text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-4 !text-base hover:bg-[#F2F4F7]"
              >
                Cancel
              </button>
              <button
                className={`!text-base text-white font-interSemiBold rounded-lg px-4 py-2.5 transition-colors ${
                  isReanalyzeEnabled
                    ? "bg-[#1751D0] hover:bg-[#113B98] cursor-pointer"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
                type="button"
                disabled={!isReanalyzeEnabled}
                onClick={handleReanalyze}
              >
                ReAnalyze
              </button>
            </div>
          </FooterModal>
        </form>
      </Modal>

      {/* Change Image Modal */}
      {showChangeImageModal && (
        <ChangeVijilizeImageModal
          isShow={showChangeImageModal}
          setIsShow={setShowChangeImageModal}
          vijilizeId={vijilizeId}
          onImageChanged={handleImageChanged}
          targetPatentId={targetPatentId}
          projectId={projectId}
          imageS3Path={imageS3Path}
          successMessage="Image selected"
        />
      )}
    </>
  );
};

export default ReAnalyzeModal;
